import styled from '@emotion/styled';
import type { Review } from '..';
import { StarIcon } from '../../../assets/icons/StarIcon';
import { VerifiedIcon } from '../../../assets/icons/VerifiedIcon';
import { formatRelativeTime } from '../../../utils/dateFormat';

const Container = styled.div`
  padding: 24px 0;
  border-bottom: 1px solid #e2e2e2;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const Divider = styled.div`
  width: 100%;
  margin: 12px 0;
  background: #f1f1f1;
  height: 1px;
`;

const VerifiedPurchase = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #737373;
  font-size: 12px;
`;

const DateText = styled.span`
  color: var(--gray-500, #737373);
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #2e2e2e;
  margin: 0 0 12px 0;
`;

const Content = styled.p`
  font-size: 14px;
  color: #6e6e6e;
  font-weight: 400;
  line-height: 1.4;
  margin: 0 0 12px 0;
`;

const ImageGrid = styled.div`
  display: flex;
  gap: 8px;
  margin: 16px 0;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (min-width: 768px) {
    overflow-x: visible;
    padding-bottom: 0;
  }
`;

const ReviewImage = styled.div<{ url: string }>`
  flex: 0 0 100px;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  background-image: url(${(props) => props.url});
  background-size: cover;
  background-position: center;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserAvatar = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: #6e6e6e;
`;

const UserName = styled.span`
  font-size: 12px;
  color: #6e6e6e;
  font-weight: 400;
`;

const StarRating = styled.div`
  display: flex;
  gap: 4px;
`;

const SkinInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;

  @media (max-width: 768px) {
    flex-flow: column;
    gap: 4px;
  }
`;

const SkinInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SkinInfoLabel = styled.span`
  color: #6e6e6e;
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const SkinInfoValue = styled.span`
  color: var(--Text-in-light-bg-25, #6e6e6e);
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

interface ReviewCardProps {
  review: Review;
  onLikeClick?: (reviewId: string) => void;
  onImageClick?: () => void;
}

export const ReviewCard = ({ review, onLikeClick, onImageClick }: ReviewCardProps) => {
  return (
    <Container>
      <Header>
        <VerifiedPurchase>
          <VerifiedIcon />
          Verified purchase
        </VerifiedPurchase>
        <DateText>{formatRelativeTime(review.createdAt)}</DateText>
      </Header>

      <Title>{review.title}</Title>
      <Content>{review.content}</Content>

      {review.images && review.images.length > 0 && (
        <ImageGrid>
          {review.images.slice(0, 4).map((image) => (
            <ReviewImage key={image.id} url={image.url} onClick={onImageClick} />
          ))}
        </ImageGrid>
      )}

      <Footer>
        <UserInfo>
          <UserAvatar>{review.author.name.charAt(0).toUpperCase()}</UserAvatar>
          <UserName>{review.author.name}</UserName>
          <StarRating>
            {[...Array(review.rating)].map((_, index) => (
              <StarIcon width={16} height={16} key={index} />
            ))}
          </StarRating>
        </UserInfo>
      </Footer>

      <Divider />

      <SkinInfoContainer>
        <SkinInfoItem>
          <SkinInfoLabel>Skin type:</SkinInfoLabel>
          <SkinInfoValue>{review.skinType.join(', ')}</SkinInfoValue>
        </SkinInfoItem>
        {review.skinConcern && (
          <SkinInfoItem>
            <SkinInfoLabel>Skin concern:</SkinInfoLabel>
            <SkinInfoValue>{review.skinConcern.join(', ')}</SkinInfoValue>
          </SkinInfoItem>
        )}
      </SkinInfoContainer>
    </Container>
  );
};

export default ReviewCard;
